import { useState, useEffect, useCallback } from "react";
import { mlQueriesService } from "../../../shared/services/apis/mlQueries";
import type { MlQuery, CreateMlQueryData, UpdateMlQueryData } from "../../../shared/services/apis/mlQueries";

interface UseMlQueriesReturn {
  riskParameters: MlQuery[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  fetchRiskParameters: () => Promise<void>;
  createRiskParameter: (data: Omit<CreateMlQueryData, 'category'>, category?: string) => Promise<void>;
  updateRiskParameter: (id: string, data: UpdateMlQueryData) => Promise<void>;
  deleteRiskParameter: (id: string) => Promise<void>;
  deleteAllRiskParameters: (tenantId: string, category?: string) => Promise<void>;
  refreshRiskParameters: () => Promise<void>;
  // New methods for category handling
  getRiskParametersByCategory: (category: string) => MlQuery[];
  getAvailableCategories: () => string[];
}

export const useMlQueries = (): UseMlQueriesReturn => {
  const [riskParameters, setRiskParameters] = useState<MlQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRiskParameters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mlQueriesService.getRiskParameters();
      setRiskParameters(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch risk parameters";
      setError(errorMessage);
      console.error("Error fetching risk parameters:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createRiskParameter = useCallback(async (data: Omit<CreateMlQueryData, 'category'>, category: string = 'RISK_PARAMETERS') => {
    try {
      setCreating(true);
      setError(null);
      const dataWithCategory = { ...data, category };
      const newRiskParameter = await mlQueriesService.createMlQuery(dataWithCategory);
      setRiskParameters((prev) => [newRiskParameter, ...prev]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create risk parameter";
      setError(errorMessage);
      throw err; // Re-throw to handle in component
    } finally {
      setCreating(false);
    }
  }, []);

  const updateRiskParameter = useCallback(async (id: string, data: UpdateMlQueryData) => {
    try {
      setUpdating(true);
      setError(null);
      const updatedRiskParameter = await mlQueriesService.updateRiskParameter(id, data);
      setRiskParameters((prev) =>
        prev.map((param) => (param.id === id ? updatedRiskParameter : param))
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update risk parameter";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deleteRiskParameter = useCallback(async (id: string) => {
    try {
      setDeleting(true);
      setError(null);
      await mlQueriesService.deleteRiskParameter(id);
      setRiskParameters((prev) => prev.filter((param) => param.id !== id));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete risk parameter";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  const deleteAllRiskParameters = useCallback(async (tenantId: string, category?: string) => {
    try {
      setDeleting(true);
      setError(null);
      if (category) {
        // Delete only for specific category
        await mlQueriesService.deleteAllRiskParametersForTenant(tenantId);
        // Filter by both tenantId and category
        setRiskParameters((prev) => 
          prev.filter((param) => !(param.tenantId === tenantId && param.category === category))
        );
      } else {
        // Delete all for tenant (all categories)
        await mlQueriesService.deleteAllRiskParametersForTenant(tenantId);
        setRiskParameters((prev) => prev.filter((param) => param.tenantId !== tenantId));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete all risk parameters";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  // New method to get risk parameters by category
  const getRiskParametersByCategory = useCallback((category: string) => {
    return riskParameters.filter(param => param.category === category);
  }, [riskParameters]);

  // New method to get available categories
  const getAvailableCategories = useCallback(() => {
    const categories = [...new Set(riskParameters.map(param => param.category || 'RISK_PARAMETERS'))];
    return categories.sort((a, b) => a.localeCompare(b));
  }, [riskParameters]);

  const refreshRiskParameters = useCallback(async () => {
    await fetchRiskParameters();
  }, [fetchRiskParameters]);

  // Initial fetch
  useEffect(() => {
    fetchRiskParameters();
  }, [fetchRiskParameters]);

  return {
    riskParameters,
    loading,
    error,
    creating,
    updating,
    deleting,
    fetchRiskParameters,
    createRiskParameter,
    updateRiskParameter,
    deleteRiskParameter,
    deleteAllRiskParameters,
    refreshRiskParameters,
    getRiskParametersByCategory,
    getAvailableCategories,
  };
};
